<script setup>
  import { ref, watch } from 'vue'
  import { onKeyStroke } from '@vueuse/core'
  import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'

  const props = defineProps({
    description: String
  })

  const isModalOpen = ref(false)
  const modalRef = ref(null)
  const triggerButtonRef = ref(null)

  // Initialize focus trap
  const { activate, deactivate } = useFocusTrap(modalRef, {
    immediate: false,
    escapeDeactivates: false, // We'll handle ESC key manually for closeModal
    allowOutsideClick: true,
  })

  const openModal = () => {
    isModalOpen.value = true
  }

  const closeModal = () => {
    isModalOpen.value = false
    // Return focus to the trigger button when modal closes
    triggerButtonRef.value?.focus()
  }

  const handleClickOutside = (event) => {
    if (modalRef.value && !modalRef.value.contains(event.target)) {
      closeModal()
    }
  }

  // Handle ESC key to close modal
  onKeyStroke('Escape', (event) => {
    if (isModalOpen.value) {
      event.preventDefault()
      closeModal()
    }
  })

  // Watch for modal open/close to manage body scroll and focus trap
  watch(isModalOpen, (newValue) => {
    if (newValue) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
      // Activate focus trap after modal opens
      setTimeout(() => {
        activate()
      }, 50)
    } else {
      // Deactivate focus trap
      deactivate()
      // Restore body scroll when modal closes
      document.body.style.overflow = ''
    }
  })
</script>

<template>
  <div>
    <button
      ref="triggerButtonRef"
      class="location-link flex flex-row ~text-sm/base items-center text-primary gap-2 underline underline-offset-4"
      type="button"
      aria-label="Hotelinfos anzeigen"
      aria-haspopup="dialog"
      :aria-expanded="isModalOpen"
      @click="openModal"
    >
      <AppIcon
        name="ArrowLink"
      />
      Hotelinfos
    </button>
    <Transition
      enter-active-class="transition-opacity duration-300"
      leave-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isModalOpen"
        class="fixed inset-0 w-full h-full bg-black/50 flex items-center justify-center z-[1000]"
        @click="handleClickOutside"
      >
        <div
          ref="modalRef"
          class="modal-content bg-white max-w-xl mx-6 max-h-[80vh] flex flex-col shadow-lg"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          tabindex="-1"
          @click.stop
        >
          <div class="flex justify-between items-center p-6">
            <h2 id="modal-title" class="m-0 text-2xl font-semibold text-gray-900">
              Hotelinfos
            </h2>
            <button
              class="bg-transparent ~text-xl/2xl text-headline cursor-pointer p-0 w-8 h-8 flex items-center justify-center rounded transition-all duration-200"
              type="button"
              aria-label="Schließen"
              @click="closeModal"
            >
              <span
                aria-hidden="true"
              >&times;</span>
            </button>
          </div>

          <div class="p-6 overflow-y-auto">
            <div
              v-if="description"
              v-html="description"
            />
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
  .modal-content :deep(p) {
    @apply mb-4 ~text-sm/base w-full max-w-none;
  }
</style>
